import { formatClinic } from "utils/clinics.util";

import { formatValidQueryParamUrlCommaSeparated } from "../utils/common";
import { createPaginatedEndpoint } from "../utils/services.util";
import { API_TAGS, emptySplitApi } from "./empty-split-api";
import {
  CardProcessingClinic,
  Clinic,
  ClinicFilters,
  ClinicMovePreview,
  ClinicMovePreviewTab,
  ClinicRequest,
  ClinicResponse,
  ClinicsResponse,
  UpdateCardProcessingAccountResponse
} from "./types/clinics.type";

// Lightweight query for initial clinic loading (clinic selector)
export const CLINICS_MINIMAL_QUERY =
  "{id,name,display_name,timezone,practice{id,name},region{id,name},type,is_active}";

// CLINICS_MINIMAL_QUERY plus the clinic's own display name (no practice fallback), for the top-bar
// clinic dropdown's CLINIC_SELECTOR_DISPLAY_NAME_FEATURE labels.
export const CLINICS_MINIMAL_WITH_OWN_DISPLAY_NAME_QUERY =
  "{id,name,display_name,own_display_name,timezone,practice{id,name},region{id,name},type,is_active}";

// Query for the Suno-only Gravity/Tilled account setup table (practice provider + clinic account ids)
export const CARD_PROCESSING_CLINICS_QUERY =
  "{id,name,street_address_1,street_address_2,city,state,zip_code,country,card_processing_provider,tilled_account_id,gravity_account_id,surcharge_percent,effective_surcharge_percent,practice{id,name,card_processing_provider,tilled_account_id,gravity_account_id,surcharge_percent}}";

// Full query for complete clinic details (loaded on selection)
export const CLINICS_QUERY =
  "{id,name,display_name,timezone,is_claim_md_account_key_set,has_active_provider_integration,practice{id,name,timezone,ein,npi,non_npi_id,non_npi_id_qualifier,is_claim_md_account_key_set,has_active_provider_integration,can_offer_online_deposit,is_messaging_enabled,is_emailing_enabled,default_appointment_reminder_duration,send_appointment_reminder_on_friday,extended_reminder_day,tilled_account_id,justifi_account_id,logo,card_processing_provider,surcharge_percent,has_quickbooks_token,is_quickbooks_enabled,scheduling_allow_overlap,scheduling_should_create_schedule_for_staff,scheduling_should_notify,products_use_supplier_price,scheduling_staff_availability,product_requests_deactivate_existing,private_feedback_request_break,google_review_feedback_request_break,appointment_information_disabled,no_reply_email,country,private_feedback_request_disabled,is_hipaa_compliance_for_quickbooks_enabled,reschedule_disabled,patient_settings,billing_name,billing_street_address_1,billing_street_address_2,billing_city,billing_state,billing_country,billing_zip_code,billing_phone,direct_mail_enabled,sale_lock_days,unit_price_lock,product_lock,collect_outcome,no_show_disabled,limit_to_current_clinic,private_feedback_textual_request_disabled,appointment_cancelled_message_disabled,suno_comms_id,has_multiple_companies,message_auto_read_enabled,save_payment_card,products_use_barcode_scanner,go_live_at,billing_use_practice_insurers_only,create_all_sales_in_draft_status,billing_available_payment_methods,billing_available_adjustment_types,billing_claim_era_sync,use_room_traffic,quickbooks_refunds_account,allowed_ips,auto_create_patient_in_noah,qbo_sync_refunds,qbo_taxes_line_item_enabled_on,use_unit_price_for_claims,allow_tilled_refund,enforce_delivery_before_ready_to_bill,form_rendering_defaults},ein,npi,non_npi_id,non_npi_id_qualifier,street_address_1,street_address_2,city,state,country,zip_code,phone,email,fax_phone,is_messaging_enabled,is_emailing_enabled,default_appointment_reminder_duration,tilled_account_id,google_place_id,review_link,justifi_account_id,card_processing_provider,surcharge_percent,effective_surcharge_percent,noah_provider,noah_alias,noah_tenant_id,scheduling_staff_availability,web_scheduling_staff_selection,no_reply_email,type,external_id,dba,opened_date,closed_date,region{id,created_at,name,is_active,noah_location_id},is_active,sms_phone,quickbooks_token{id,name,description,quickbooks_company{id,realm_id,company_name},quickbooks_product_services_settings},logo,extra{id,billng_street_address_1,billng_street_address_2,billng_city,billng_state,billng_country,billng_zip,billng_phone},quickbooks_realm_id,suno_comms_id,quickbooks_company{id,realm_id,company_name},noah_auto_sync,web_scheduler_email_recipient,box_folder_id,place_of_service,default_assignee,caption_call_referral_code,five_keys_url,business_name}";

// Admin-panel clinics list (SN-19777): the backend computes `patients_count` and
// `upcoming_appointments_count` only when the RestQL query names them (a per-row COUNT each),
// so the list opts into them on top of whichever base query it uses.
export const withClinicCounts = (query: string) =>
  query.replace(/}$/, ",patients_count,upcoming_appointments_count}");
export const CLINICS_QUERY_WITH_PATIENTS_COUNT =
  withClinicCounts(CLINICS_QUERY);

// Full field set for the Suno admin-mode clinic modal (edit + duplicate).
// Includes read-only/system fields for display and the Suno-only fields
// exposed by the backend (noah_business_admin, noah_location_id,
// is_documo_account_key_set, is_claim_md_account_key_set).
// Both provider credentials are write-only: the query asks for the `is_*_set` booleans, never the
// keys themselves (SN-19151).
//
// Every field the modal EDITS must be an `own_*` field where one exists. `display_name` and
// `default_appointment_reminder_duration` resolve through the practice when the clinic has nothing
// set, so rendering them into inputs and PATCHing them back stamps the practice's value onto the
// clinic. The `own_*` variants read and write the clinic's own columns only.
export const CLINICS_ADMIN_QUERY =
  "{id,name,display_name,own_display_name,timezone,practice{id,name,country,is_quickbooks_enabled},own_ein,own_npi,own_non_npi_id,own_non_npi_id_qualifier,street_address_1,street_address_2,city,state,country,zip_code,phone,email,sms_phone,additional_sms_phones,fax_phone,no_reply_email,google_place_id,review_link,is_messaging_enabled,is_emailing_enabled,own_default_appointment_reminder_duration,type,external_id,dba,opened_date,closed_date,region{id,name},is_active,scheduling_staff_availability,web_scheduling_staff_selection,place_of_service,billing_name,intake_form_completion_email_recipients,caption_call_referral_code,five_keys_url,business_name,noah_provider,noah_alias,noah_tenant_id,noah_auto_sync,noah_location_id,is_documo_account_key_set,is_claim_md_account_key_set,has_active_provider_integration,card_processing_provider,tilled_account_id,justifi_account_id,gravity_account_id,quickbooks_realm_id,suno_comms_id,box_folder_id,quickbooks_token{id,name,description},quickbooks_company{id,realm_id,company_name},default_assignee{id,first_name,last_name},web_scheduler_email_recipient{id,first_name,last_name},web_scheduler_email_recipients{id,first_name,last_name},noah_business_admin{id,first_name,last_name},users{id,first_name,last_name},extra{id,billng_street_address_1,billng_street_address_2,billng_city,billng_state,billng_country,billng_zip,billng_phone,ontario_adp_vendor_reg_number,include_tympanometry_graph,tympanometry_scale_max,tympanometry_scale_step,instructions}}";

// Administration → Clinics with patient text routing (INBOX_TEXT_ROUTING_FEATURE) on: the
// list's own CLINICS_QUERY with the clinic's inbox owner (`default_assignee`) projected to the
// fields the "Inbox owner" column shows. Flag off => the list keeps requesting CLINICS_QUERY.
export const CLINICS_QUERY_WITH_INBOX_OWNER = CLINICS_QUERY.replace(
  ",default_assignee,",
  ",default_assignee{id,first_name,last_name},"
);

export const clinicsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getClinicsPaginated: createPaginatedEndpoint<
      ClinicFilters,
      ClinicResponse[]
    >(builder, {
      queryFn: (body) => ({
        url: "/clinics/",
        params: { ...body, ordering: "name,id", query: CLINICS_QUERY }
      }),
      providesTags: ["Clinics"],
      transformResponseFn: (response: ClinicResponse[]) =>
        response.map(formatClinic)
    }),
    getClinics: builder.query<ClinicsResponse, ClinicFilters>({
      query: ({ query = CLINICS_QUERY, ...filters }) =>
        `/clinics/?ordering=name,id&query=${query}&${formatValidQueryParamUrlCommaSeparated(
          filters
        )}`,
      providesTags: ["Clinics"],
      transformResponse: (response: ClinicResponse[]) =>
        response.map(formatClinic)
    }),
    getClinic: builder.query<Clinic, { clinicId: number; query?: string }>({
      query: ({ clinicId, query = CLINICS_QUERY }) =>
        `/clinics/${clinicId}/?query=${query}`,
      providesTags: ["Clinics"],
      transformResponse: (response: ClinicResponse) => formatClinic(response)
    }),
    createClinic: builder.mutation<Clinic, ClinicRequest>({
      query: (body) => ({
        url: "/clinics/",
        method: "POST",
        body: { ...body, quickbooks_token: body.quickbooks_token?.id }
      }),
      invalidatesTags: ["Clinics"]
    }),
    updateClinic: builder.mutation<
      Clinic,
      Partial<ClinicRequest> & { id: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/clinics/${id}/`,
        method: "PATCH",
        body: { ...body, quickbooks_token: body.quickbooks_token?.id }
      }),
      invalidatesTags: (result, _, { users, products }) => {
        const tags: (typeof API_TAGS)[number][] = ["Clinics"];

        if (users) {
          tags.push("Staff");
          tags.push("StaffLicenseDefaults");
        }

        if (products) {
          tags.push("Products");
        }

        return tags;
      }
    }),
    updateClinicLogo: builder.mutation<
      Clinic,
      { id: number; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: `/clinics/${id}/`,
        method: "PATCH",
        body: formData
      }),
      transformResponse: (response: ClinicResponse) => formatClinic(response),
      // Unconditional on purpose: the logo is part of the shared CLINICS_QUERY
      // (`clinic.logo`), so every screen reading that query needs the refetch to
      // show a changed logo. (The narrow, conditional counterpart to compare
      // against is `updatePractice` in services/practices.ts, not `updateClinic`
      // above — that one also always invalidates Clinics.)
      invalidatesTags: ["Clinics"]
    }),
    getUnreadFaxCount: builder.query<{ count: number }, { clinicId: number }>({
      query: ({ clinicId }) => `/clinics/${clinicId}/faxes/unread-fax-count/`,
      providesTags: ["Faxes", "FaxesCount"]
    }),
    readAllFaxes: builder.mutation<
      { updated_count: number },
      { clinicId: number }
    >({
      query: ({ clinicId }) => ({
        url: `/clinics/${clinicId}/faxes/read-all/`,
        method: "POST"
      }),
      invalidatesTags: ["FaxesCount"]
    }),
    getCardProcessingClinics: builder.query<CardProcessingClinic[], void>({
      query: () =>
        `/clinics/?ordering=name&query=${CARD_PROCESSING_CLINICS_QUERY}`,
      providesTags: ["Clinics"]
    }),
    updateClinicCardProcessingAccount: builder.mutation<
      UpdateCardProcessingAccountResponse,
      { id: number; account_id: string; card_processing_provider: number }
    >({
      query: ({ id, account_id, card_processing_provider }) => ({
        url: `/clinics/${id}/card-processing-account/`,
        method: "PATCH",
        body: { account_id, card_processing_provider }
      }),
      invalidatesTags: ["Clinics"]
    }),
    updatePracticeCardProcessingProvider: builder.mutation<
      { id: number; card_processing_provider: number },
      { id: number; card_processing_provider: number }
    >({
      query: ({ id, card_processing_provider }) => ({
        url: `/practices/${id}/card-processing-provider/`,
        method: "PATCH",
        body: { card_processing_provider }
      }),
      invalidatesTags: ["Clinics"]
    }),
    updatePracticeSurcharge: builder.mutation<
      { id: number; surcharge_percent: string | number },
      { id: number; surcharge_percent: number }
    >({
      query: ({ id, surcharge_percent }) => ({
        url: `/practices/${id}/card-processing-surcharge/`,
        method: "PATCH",
        body: { surcharge_percent }
      }),
      invalidatesTags: ["Clinics"]
    }),
    // GET on the move URL (SN-19777): one page of the patients or upcoming appointments the move
    // covers, for the modal's Patients / Appointments tabs. `patient_search` matches patient names
    // (`search` is the clinic list's own name filter and would 404 the clinic).
    getClinicMovePreview: builder.query<
      ClinicMovePreview,
      {
        clinicId: number;
        tab: ClinicMovePreviewTab;
        limit?: number;
        offset?: number;
        patient_search?: string;
      }
    >({
      // `patient_search` is free text: encode it like getStaffPaginated does (review D10).
      query: ({ clinicId, patient_search, ...params }) =>
        `/clinics/${clinicId}/move-patients/?${formatValidQueryParamUrlCommaSeparated(
          {
            ...params,
            patient_search: patient_search
              ? encodeURIComponent(patient_search)
              : undefined
          }
        )}`,
      providesTags: ["Patients", "Appointments"]
    }),
    movePatientsToClinic: builder.mutation<
      // bulk_action_id: the run's row in Bulk Action History (undoable there).
      {
        moved_count: number;
        moved_appointments_count: number;
        /** Visits that would cross midnight in the destination's timezone stay behind (review D2). */
        left_behind_appointments_count: number;
        bulk_action_id: number;
      },
      // move_appointments: also move the clinic's upcoming appointments (default true on the backend).
      // notify_patients / sync_integrations: follow-ups on the moved appointments, notify defaults off, sync_integrations defaults on.
      // exclude_*_ids: the preview rows the admin unticked (an excluded patient keeps their appointments).
      {
        fromClinicId: number;
        to_clinic: number;
        move_appointments: boolean;
        notify_patients?: boolean;
        sync_integrations?: boolean;
        exclude_patient_ids?: number[];
        exclude_appointment_ids?: number[];
      }
    >({
      query: ({ fromClinicId, ...body }) => ({
        url: `/clinics/${fromClinicId}/move-patients/`,
        method: "POST",
        body
      }),
      invalidatesTags: [
        "Clinics",
        "Patients",
        "Appointments",
        "Events",
        "BulkActions"
      ]
    }),
    updateClinicSurcharge: builder.mutation<
      {
        id: number;
        surcharge_percent: string | number;
        effective_surcharge_percent: string | number;
        updated_clinic_ids: number[];
      },
      { id: number; surcharge_percent: number; apply_to_all_clinics?: boolean }
    >({
      query: ({ id, surcharge_percent, apply_to_all_clinics }) => ({
        url: `/clinics/${id}/card-processing-surcharge/`,
        method: "PATCH",
        body: { surcharge_percent, apply_to_all_clinics }
      }),
      invalidatesTags: ["Clinics"]
    })
  })
});

export const {
  useGetClinicsQuery,
  useLazyGetClinicsQuery,
  useGetClinicQuery,
  useLazyGetClinicQuery,
  useCreateClinicMutation,
  useUpdateClinicMutation,
  useUpdateClinicLogoMutation,
  useGetUnreadFaxCountQuery,
  useReadAllFaxesMutation,
  useGetCardProcessingClinicsQuery,
  useUpdateClinicCardProcessingAccountMutation,
  useUpdatePracticeCardProcessingProviderMutation,
  useUpdatePracticeSurchargeMutation,
  useUpdateClinicSurchargeMutation,
  useMovePatientsToClinicMutation,
  useGetClinicMovePreviewQuery
} = clinicsApi;
